/* ═══════════════════════════════════════════════════════════════
   The Designer — the canvas.

   One continuous editable document, styled as the forma's paper.
   Click anywhere and type. The caret, selection, undo history, IME
   and clipboard belong to ProseMirror; the spec rules on top belong
   to numbering.ts and commands.ts. Nothing here is hand-rolled.

   R5 — NO MODES. The body is always a text editor and the header,
   footer, pre-body and post-body bands are always zone objects: click
   a band to edit it, drag tokens onto it, right there, while the caret
   is still sitting in a paragraph. Clicking body text NEVER means
   anything but "put the caret here."
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import type { ContentBlock, RevisionSettings, StandardConfig } from '../../shared/types';
import { DEFAULT_REVISION_SETTINGS } from '../../shared/types';
import { DESIGNER_NODES, blocksToDoc, docToBlocks } from './schema';
import { Numbering, REBUILD_LAYOUT } from './numbering';
import { Revisions, REBUILD_REVISIONS } from './revisions';
import { SpecKeymap, UniqueBlockIds } from './commands';
import { DesignerSearch } from './search';
import { SmartBlockView, SpecImageView } from './nodeviews';
import { TableToolbar } from './TableToolbar';
import { EndOfSection, ZoneBand, paperVars } from './paper';
import { BlockMenu, type BlockMenuState } from './BlockMenu';
import type { DesignWiring, ZoneDropData } from './Workspace';
import {
  BAND_LABEL,
  COLUMN_LABEL,
  bandVariantName,
  readZone,
  zoneKeyFor,
  type BandKind,
  type ZoneColumn,
} from './formaZonesModel';

export interface DesignerCanvasProps {
  /** Identity of the loaded document — changing it reloads the canvas.
   *  Viewing a snapshot passes a different key than live editing, so the
   *  undo histories never bleed into each other. */
  specId: string;
  blocks: ContentBlock[];
  forma: StandardConfig | null;
  specNumber: string;
  specName: string;
  projectMeta: Record<string, string>;
  zoom: number;
  /** Fired on every document change, already converted to storage shape. */
  onChange: (blocks: ContentBlock[]) => void;
  /** Ctrl+S. */
  onSave: () => void;
  /** Escape left the document — move focus somewhere sensible in the shell. */
  onEscape?: () => void;
  /** So the shell can drive undo/redo and read `can()` state. */
  onEditorReady?: (editor: Editor | null) => void;
  /** Read-only when viewing history or without edit permission. */
  editable?: boolean;
  /** Zone wiring. Present → the paper's bands are live objects (click to
      select, drop tokens with snapping columns). The BODY is unaffected: it
      is a text editor either way. Absent → a read-only paper (viewing an
      issuance, or no edit rights). */
  design?: DesignWiring;
  /** Right-click ▸ "Level style…" — hand the level to the Levels palette. */
  onLevelStyle?: (level: number) => void;
  /** The revision-mark baseline (flattened printable blocks). Null = design
   *  phase / no baseline — no marks render. */
  baselineBlocks?: ContentBlock[] | null;
  /** The architect's punch-in mark styles (migration 033). */
  revisionSettings?: RevisionSettings;
}

/* ─── One band of the paper as a Design-mode object: click to select the
       zone, drag a chip over it to get three SOLID column targets with a
       snap highlight and an insertion caret. ─── */
export function ZoneShell({
  band,
  design,
  empty,
  children,
}: {
  band: BandKind;
  design: DesignWiring;
  /** No tokens in any of this band's three columns (for the placeholder). */
  empty: boolean;
  children: React.ReactNode;
}) {
  const [overCol, setOverCol] = React.useState<ZoneColumn | null>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const selected = design.styleSel?.kind === 'zone' && design.styleSel.band === band;

  const parseDrag = (e: React.DragEvent): ZoneDropData | null => {
    try {
      const raw = e.dataTransfer.getData('application/json');
      if (!raw) return null;
      return JSON.parse(raw) as ZoneDropData;
    } catch {
      return null;
    }
  };

  const colFromX = (e: React.DragEvent): ZoneColumn => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const t = (e.clientX - r.left) / Math.max(1, r.width);
    return t < 1 / 3 ? 'L' : t < 2 / 3 ? 'C' : 'R';
  };

  const finishDrop = (e: React.DragEvent, col: ZoneColumn) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    setOverCol(null);
    const data = parseDrag(e);
    if (data) design.onZoneDrop(band, col, data);
  };

  const showTargets = design.dragActive || dragOver;

  return (
    <div
      className={[
        'dsg-zoneshell',
        `dsg-zoneshell-${band}`,
        selected ? 'is-selected' : '',
        showTargets ? 'is-droppable' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="button"
      tabIndex={0}
      aria-label={`${BAND_LABEL[band]} band — click to edit`}
      aria-pressed={selected}
      // Stop at mousedown too: the sheet clears the zone selection on
      // mousedown, and a band must not clear itself on the way to selecting.
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        design.onStyleSelect({ kind: 'zone', band });
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          design.onStyleSelect({ kind: 'zone', band });
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
        setOverCol(colFromX(e));
      }}
      onDragLeave={(e) => {
        if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
          setDragOver(false);
          setOverCol(null);
        }
      }}
      onDrop={(e) => finishDrop(e, overCol ?? colFromX(e))}
    >
      <span className="dsg-zoneshell-tag" aria-hidden="true">
        {BAND_LABEL[band]}
      </span>
      {empty ? (
        // Order 1 (Josh, 2026-08-13): the Studio has no zone panel — the
        // empty-band hint there adds text right on the sheet. One button per
        // COLUMN, so an empty band's Left and Right are reachable without a
        // drag (they used to be, through the classic canvas's three slots).
        // The Designer passes no `onZoneQuickAdd`, so its empty-hint stays
        // exactly the plain, non-interactive div it always was.
        design.onZoneQuickAdd ? (
          <div className="dsg-zoneshell-empty dsg-zoneshell-empty-cols">
            <span className="dsg-zoneshell-empty-label">
              {BAND_LABEL[band]} is empty — add text
            </span>
            <span className="dsg-zoneshell-empty-actions">
              {(['L', 'C', 'R'] as ZoneColumn[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  className="dsg-zoneshell-empty-btn"
                  aria-label={`Add text to the ${COLUMN_LABEL[c].toLowerCase()} of the ${BAND_LABEL[band].toLowerCase()}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    design.onStyleSelect({ kind: 'zone', band });
                    design.onZoneQuickAdd?.(band, c);
                  }}
                >
                  {COLUMN_LABEL[c]}
                </button>
              ))}
            </span>
          </div>
        ) : (
          <div className="dsg-zoneshell-empty">
            {BAND_LABEL[band]} is empty — drop tokens here, or click to edit
          </div>
        )
      ) : (
        children
      )}
      {showTargets && (
        <div className="dsg-zonedrop" aria-hidden="true">
          {(['L', 'C', 'R'] as ZoneColumn[]).map((c) => (
            <div
              key={c}
              className={`dsg-zonedrop-col${overCol === c ? ' is-over' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(true);
                setOverCol(c);
              }}
              onDrop={(e) => finishDrop(e, c)}
            >
              <span className="dsg-zonedrop-label">
                {overCol === c ? `Add to ${COLUMN_LABEL[c].toLowerCase()}` : COLUMN_LABEL[c]}
              </span>
              {overCol === c && <span className="dsg-zonedrop-caret" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DesignerCanvas({
  specId,
  blocks,
  forma,
  specNumber,
  specName,
  projectMeta,
  zoom,
  onChange,
  onSave,
  onEscape,
  onEditorReady,
  editable = true,
  design,
  onLevelStyle,
  baselineBlocks = null,
  revisionSettings,
}: DesignerCanvasProps) {
  // One surface: the body is a text editor whenever the user may edit it,
  // regardless of what the zone bands are doing at the same moment.
  const canType = editable;
  // The plugins read the forma through a ref so a forma edit restyles the
  // paper without tearing down and rebuilding the editor (which would drop
  // the caret and the undo history).
  const formaRef = React.useRef<StandardConfig | null>(forma);
  formaRef.current = forma;

  // Same pattern for the revision baseline + punch-in styles: a settings
  // tweak restyles the marks without rebuilding the editor.
  const baselineRef = React.useRef<ContentBlock[] | null>(baselineBlocks);
  baselineRef.current = baselineBlocks;
  const settingsRef = React.useRef<RevisionSettings>(revisionSettings || DEFAULT_REVISION_SETTINGS);
  settingsRef.current = revisionSettings || DEFAULT_REVISION_SETTINGS;

  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const onSaveRef = React.useRef(onSave);
  onSaveRef.current = onSave;
  const onEscapeRef = React.useRef(onEscape);
  onEscapeRef.current = onEscape;

  const editor = useEditor(
    {
      extensions: [
        ...DESIGNER_NODES,
        DesignerSearch,
        UniqueBlockIds,
        Numbering.configure({ getForma: () => formaRef.current }),
        Revisions.configure({
          getBaseline: () => baselineRef.current,
          getSettings: () => settingsRef.current,
        }),
        SpecKeymap.configure({
          onSave: () => onSaveRef.current(),
          onEscape: () => onEscapeRef.current?.(),
        }),
      ],
      content: blocksToDoc(blocks),
      editable: canType,
      editorProps: {
        attributes: {
          class: 'dsg-doc',
          role: 'textbox',
          'aria-multiline': 'true',
          'aria-label': `${specNumber} ${specName} — specification text`,
        },
        nodeViews: {
          // The table's NodeView is wired in automatically by the framework's
          // own columnResizing plugin (tables.ts's SpecTable sets `View:`) —
          // nothing to register here.
          specImage: (node) => new SpecImageView(node),
          smartBlock: (node) => new SmartBlockView(node),
        },
      },
      onUpdate: ({ editor: ed, transaction }) => {
        if (!transaction.docChanged) return;
        onChangeRef.current(docToBlocks(ed.getJSON()));
      },
    },
    // Rebuilding on specId is deliberate: switching sections IS a new
    // document, and its undo history must not bleed across sections.
    [specId]
  );

  React.useEffect(() => {
    onEditorReady?.(editor ?? null);
    return () => onEditorReady?.(null);
  }, [editor, onEditorReady]);

  React.useEffect(() => {
    if (editor) editor.setEditable(canType);
  }, [editor, canType]);

  // A forma change restyles every paragraph — ask the layout plugin to
  // re-derive without touching the document.
  React.useEffect(() => {
    if (!editor) return;
    editor.view.dispatch(editor.state.tr.setMeta(REBUILD_LAYOUT, true));
  }, [editor, forma]);

  // A baseline or punch-in change re-derives the marks the same way.
  React.useEffect(() => {
    if (!editor) return;
    editor.view.dispatch(editor.state.tr.setMeta(REBUILD_REVISIONS, true));
  }, [editor, baselineBlocks, revisionSettings]);

  // ─── Right-click on a paragraph ───
  const [menu, setMenu] = React.useState<BlockMenuState | null>(null);
  const onContextMenu = (e: React.MouseEvent) => {
    if (!editor) return;
    const block = (e.target as HTMLElement).closest('[data-block-id]');
    if (!block) return;
    e.preventDefault();
    setMenu({
      x: e.clientX,
      y: e.clientY,
      level: Number(block.getAttribute('data-level')) || 1,
      excluded: block.classList.contains('dsg-excluded'),
    });
  };

  const paperContext = { forma, specNumber, specName, projectMeta };

  // Which page variant's bands the paper shows — the one being edited. With
  // no zone wiring (read-only paper) it falls back to page 1's, as before.
  const bandVariant = design ? bandVariantName(design.pageVariant) : undefined;

  const bandEmpty = React.useCallback(
    (band: BandKind): boolean => {
      if (!forma || !design) return false;
      return (['L', 'C', 'R'] as ZoneColumn[]).every(
        (c) => readZone(forma, zoneKeyFor(band, design.pageVariant, c)).length === 0
      );
    },
    [forma, design]
  );

  // Clicking anywhere that is NOT a band drops the zone selection — one
  // selection system, never sticky (DESIGN §3.4). It does nothing else:
  // the click still places the caret, because that is all a click on body
  // text has ever been allowed to mean.
  const onSheetMouseDown = () => {
    if (design?.styleSel) design.onStyleSelect(null);
  };

  const wrapBand = (band: BandKind, node: React.ReactNode) =>
    design ? (
      <ZoneShell band={band} design={design} empty={bandEmpty(band)}>
        {node}
      </ZoneShell>
    ) : (
      node
    );

  const sheetClasses = [
    'dsg-sheet',
    design ? 'dsg-zones-live' : '',
    // The Levels palette points at a level → every paragraph of it lights up,
    // so "what am I restyling" is never a guess.
    design?.levelFocus ? `dsg-sel-l${design.levelFocus}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className="dsg-canvas"
      style={{ ...paperVars(forma) }}
      onMouseDown={(e) => {
        // Click-away on the stage (outside the sheet) clears the selection.
        if (design && e.target === e.currentTarget) design.onStyleSelect(null);
      }}
    >
      <div
        className={sheetClasses}
        onContextMenu={onContextMenu}
        onMouseDown={onSheetMouseDown}
        style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
      >
        <div className="dsg-sheet-header">
          {wrapBand('header', <ZoneBand kind="header" variant={bandVariant} {...paperContext} />)}
        </div>

        <div className="dsg-sheet-body">
          {wrapBand('pre', <ZoneBand kind="pre" {...paperContext} />)}
          <EditorContent editor={editor} />
          {wrapBand('post', <ZoneBand kind="post" {...paperContext} />)}
          <EndOfSection {...paperContext} />
        </div>

        <div className="dsg-sheet-footer">
          {wrapBand('footer', <ZoneBand kind="footer" variant={bandVariant} {...paperContext} />)}
        </div>
      </div>

      {menu && editor && (
        <BlockMenu
          editor={editor}
          state={menu}
          canEdit={editable}
          onLevelStyle={onLevelStyle}
          onClose={() => {
            setMenu(null);
            editor.commands.focus();
          }}
        />
      )}

      {/* The table context toolbar: appears only while the selection sits
          inside a table, anchored under it — §4 R7. */}
      {editor && <TableToolbar editor={editor} canEdit={editable} />}
    </div>
  );
}
